import { Icon } from '@design/components/icon/Icon';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useEntity } from '@terminal/react-query-hooks/entities/useEntities';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import clsx from 'clsx';
import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PulseCompanyCell } from '../components/pulse-company-cell/PulseCompanyCell';
import { PulseSentimentInsight } from '../pulse-types';
import { CategoryPill } from './category-pill/CategoryPill';
import styles from './PulseInsightCard.module.scss';
import { CompanyOneDayReaction } from './reaction/CompanyOneDayReaction';
import { ReactionMetric } from './reaction/ReactionMetric';
import { formatSignedPercent, SignedChange } from './reaction/SignedChange';
import { ThemeOneDayReaction } from './reaction/ThemeOneDayReaction';

export const PulseInsightCard = ({
  insight,
  source,
  onOpen,
}: {
  insight: PulseSentimentInsight;
  source: PosthogTrackingSource;
  onOpen?: () => void;
}) => {
  const { t } = useTranslation(['widget']);
  const [isExpanded, setIsExpanded] = useState(false);

  const { navigateToAssetOverview } = useNavigateToAssetOverview();
  const { data: companyData } = useEntity(insight.company?.entityTag);
  const companyEntity = companyData?.data;

  const metricValue = insight.reactionPercent;
  const metricKey = insight.scenario
    ? 'widget:themeOverview.pulse.drawer.cumulativeReturn'
    : 'widget:themeOverview.pulse.drawer.oneDayReaction';

  const toggleExpanded = (event: MouseEvent) => {
    event.stopPropagation();
    setIsExpanded(expanded => !expanded);
  };

  return (
    <article
      className={styles.root}
      // TODO: Handle scenario cards
      onClick={onOpen}
      data-testid="pulse-insight-card"
    >
      <header className={styles.header}>
        <CategoryPill insight={insight} companyEntity={companyEntity} />
        {insight.subjectThemeId ? (
          <ThemeOneDayReaction themeId={insight.subjectThemeId} />
        ) : metricValue !== undefined ? (
          <ReactionMetric i18nKey={metricKey} value={metricValue} />
        ) : (
          companyEntity && <CompanyOneDayReaction entity={companyEntity} />
        )}
      </header>
      <h4 className={styles.title}>{insight.title}</h4>
      <p className={styles.summary}>{insight.summary}</p>
      {insight.scenario && (
        <div className={styles.scenarioRow}>
          <SignedChange value={insight.scenario.episodeReturnPercent}>
            {formatSignedPercent({
              value: insight.scenario.episodeReturnPercent,
              useOriginalValue: false,
            })}
          </SignedChange>
          <span className={styles.pillSeparator}>·</span>
          <span className={styles.horizon}>{insight.scenario.horizon}</span>
          <span className={styles.pillSeparator}>·</span>
          <span className={styles.starRating}>
            <Icon
              iconName="StarFill"
              size={12}
              fillColor="var(--icon-primary)"
            />
            {insight.scenario.starRating}
          </span>
        </div>
      )}
      {insight.drivenBy.length > 0 && (
        <div className={styles.drivenBy}>
          <button
            type="button"
            className={styles.drivenByToggle}
            onClick={toggleExpanded}
            aria-expanded={isExpanded}
          >
            {t('widget:themeOverview.pulse.drawer.drivenByCompanies', {
              count: insight.drivenBy.length,
            })}
            <Icon
              iconName={isExpanded ? 'ChevronLightUp' : 'ChevronLightDown'}
              size={12}
            />
          </button>
          {isExpanded && (
            <div className={styles.drivenByList}>
              {insight.drivenBy.map(driver => (
                <div
                  key={driver.entityTag}
                  className={styles.driverRow}
                  onClick={e => {
                    e.stopPropagation();
                    navigateToAssetOverview(driver.entityTag, source);
                  }}
                >
                  <div className={styles.driverCompany}>
                    <PulseCompanyCell company={driver} layout="inline" />
                  </div>
                  <span
                    className={clsx(styles.driverSentiment, {
                      [styles.negative]: driver.sentiment === 'bearish',
                    })}
                  >
                    {t(
                      `widget:themeOverview.pulse.drawer.sentiment_${driver.sentiment}`
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </article>
  );
};
